import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Typography, Button, IconButton, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Paper, Chip, Menu, MenuItem, ListItemIcon,
  ListItemText, Tooltip, InputAdornment, CircularProgress, Divider, Avatar,
  Checkbox, Tabs, Tab, Snackbar, Alert,
} from '@mui/material';
import {
  Add as AddIcon, Description as DocIcon, TableChart as SheetIcon,
  Search as SearchIcon, MoreVert as MoreIcon, Delete as DeleteIcon,
  Edit as EditIcon, Share as ShareIcon, Link as LinkIcon,
  ContentCopy as CopyIcon, Download as DownloadIcon,
  RestoreFromTrash as RestoreIcon, DeleteForever as PermanentDeleteIcon,
  CloudUpload as UploadIcon, GridView as GridViewIcon,
  ViewList as ListViewIcon, OpenInNew as OpenIcon,
  Star as StarIcon, StarBorder as StarBorderIcon,
  Apps as AppsIcon, AccessTime as RecentIcon,
  PeopleAlt as SharedIcon, Sort as SortIcon,
  ArrowDropDown as ArrowDropDownIcon,
  FileCopy as DuplicateIcon,
} from '@mui/icons-material';
import api, { extractData, handleApiError } from '../../utils/api';
import { useAuthStore } from '../../stores/authStore';

interface SharedDoc {
  id: string;
  title: string;
  type: string;
  fileKey: string;
  fileSize: number;
  ownerId: string;
  ownerEmail?: string;
  sharedWith: Array<{ userId: string; email: string; permission: string; sharedAt: string }>;
  isShareLinkEnabled: boolean;
  shareLink?: string;
  shareLinkPermission?: string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

interface DocsPageProps { documentType: 'document' | 'spreadsheet'; }

type SectionTab = 'all' | 'recent' | 'starred' | 'shared' | 'trash';
type SortKey = 'updated' | 'title' | 'size';

const TEMPLATES_DOC = [
  { key: 'resume',   label: 'Resume',         tag: 'Serif',     accent: '#5b8def' },
  { key: 'letter',   label: 'Letter',         tag: 'Modern',    accent: '#7c4dff' },
  { key: 'proposal', label: 'Project proposal', tag: 'Tropic',  accent: '#26a69a' },
  { key: 'meeting',  label: 'Meeting notes',  tag: 'Action items', accent: '#ef6c00' },
  { key: 'report',   label: 'Report',         tag: 'Luxe',      accent: '#c2185b' },
];

const TEMPLATES_SHEET = [
  { key: 'budget',    label: 'Annual budget',     tag: 'Personal',  accent: '#0f9d58' },
  { key: 'tracker',   label: 'Project tracker',   tag: 'Team',      accent: '#1a73e8' },
  { key: 'invoice',   label: 'Invoice',           tag: 'Modern',    accent: '#7c4dff' },
  { key: 'todo',      label: 'To-do list',        tag: 'Simple',    accent: '#ef6c00' },
  { key: 'planner',   label: 'Schedule',          tag: 'Weekly',    accent: '#26a69a' },
];

const formatSize = (b: number) => {
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
};

const formatRelative = (iso: string) => {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)} min ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)} hr ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)} d ago`;
  return d.toLocaleDateString();
};

const DocsPage: React.FC<DocsPageProps> = ({ documentType }) => {
  const { user } = useAuthStore();
  const userId = (user as any)?.id || (user as any)?.email || 'anon';
  const isSheet = documentType === 'spreadsheet';
  const accent = isSheet ? '#0f9d58' : '#1a73e8';
  const accentSoft = isSheet ? 'rgba(15,157,88,0.08)' : 'rgba(26,115,232,0.08)';
  const typeWord = isSheet ? 'Spreadsheet' : 'Document';
  const typeWordPlural = isSheet ? 'Spreadsheets' : 'Documents';
  const TypeIcon = isSheet ? SheetIcon : DocIcon;
  const templates = isSheet ? TEMPLATES_SHEET : TEMPLATES_DOC;
  const starredKey = `docs.starred.${userId}.${documentType}`;

  const [docs, setDocs] = useState<SharedDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [section, setSection] = useState<SectionTab>('all');
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [sortKey, setSortKey] = useState<SortKey>('updated');
  const [sortAsc, setSortAsc] = useState(false);
  const [sortMenu, setSortMenu] = useState<HTMLElement | null>(null);
  const [newMenu, setNewMenu] = useState<HTMLElement | null>(null);
  const [showTemplates, setShowTemplates] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dragOver, setDragOver] = useState(false);
  const [snack, setSnack] = useState<{ msg: string; sev: 'success' | 'error' | 'info' } | null>(null);
  const [starred, setStarred] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem(starredKey) || '[]')); } catch { return new Set(); }
  });
  const searchRef = useRef<HTMLInputElement | null>(null);
  const dragCounter = useRef(0);

  // Dialogs
  const [createDialog, setCreateDialog] = useState<{ open: boolean; title: string }>({ open: false, title: '' });
  const [renameDialog, setRenameDialog] = useState<{ open: boolean; doc: SharedDoc | null; title: string }>({ open: false, doc: null, title: '' });
  const [shareDialog, setShareDialog] = useState<{ open: boolean; doc: SharedDoc | null }>({ open: false, doc: null });
  const [shareEmail, setShareEmail] = useState('');
  const [sharePerm, setSharePerm] = useState<'view' | 'edit'>('view');
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; doc: SharedDoc } | null>(null);

  // Persist starred set
  useEffect(() => {
    localStorage.setItem(starredKey, JSON.stringify(Array.from(starred)));
  }, [starred, starredKey]);

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('type', documentType);
      params.set('includeShared', 'true');
      params.set('includeDeleted', 'true');
      const res = await api.get(`/documents?${params.toString()}`);
      setDocs(extractData<SharedDoc[]>(res));
    } catch (err) {
      console.error(handleApiError(err));
      setSnack({ msg: handleApiError(err), sev: 'error' });
    } finally { setLoading(false); }
  }, [documentType]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      const inField = tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable;
      if (e.key === '/' && !inField) { e.preventDefault(); searchRef.current?.focus(); return; }
      if (e.key === 'Escape' && selected.size > 0) { setSelected(new Set()); return; }
      if (e.key === 'Delete' && selected.size > 0 && !inField) {
        e.preventDefault();
        bulkTrash();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // Filter + sort
  const filtered = useMemo(() => {
    let list = docs.slice();
    if (section === 'trash') list = list.filter(d => d.isDeleted);
    else list = list.filter(d => !d.isDeleted);
    if (section === 'starred') list = list.filter(d => starred.has(d.id));
    if (section === 'shared') list = list.filter(d => d.ownerId !== (user as any)?.id);
    if (section === 'recent') {
      list = list.sort((a, b) => +new Date(b.updatedAt) - +new Date(a.updatedAt)).slice(0, 20);
    }
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      list = list.filter(d =>
        d.title.toLowerCase().includes(s) ||
        (d.ownerEmail || '').toLowerCase().includes(s)
      );
    }
    if (section !== 'recent') {
      list.sort((a, b) => {
        let cmp = 0;
        if (sortKey === 'title') cmp = a.title.localeCompare(b.title);
        else if (sortKey === 'size') cmp = a.fileSize - b.fileSize;
        else cmp = +new Date(a.updatedAt) - +new Date(b.updatedAt);
        return sortAsc ? cmp : -cmp;
      });
    }
    return list;
  }, [docs, section, search, sortKey, sortAsc, starred, user]);

  const counts = useMemo(() => ({
    all: docs.filter(d => !d.isDeleted).length,
    recent: Math.min(20, docs.filter(d => !d.isDeleted).length),
    starred: docs.filter(d => !d.isDeleted && starred.has(d.id)).length,
    shared: docs.filter(d => !d.isDeleted && d.ownerId !== (user as any)?.id).length,
    trash: docs.filter(d => d.isDeleted).length,
  }), [docs, starred, user]);

  const openDoc = (id: string) => window.open(`/editor/${id}`, '_blank', 'noopener');

  const toggleStar = (id: string) => {
    setStarred(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const createBlank = async (title?: string) => {
    try {
      const res = await api.post('/documents', {
        title: title || (isSheet ? 'Untitled spreadsheet' : 'Untitled document'),
        type: documentType,
      });
      const doc = extractData<SharedDoc>(res);
      setCreateDialog({ open: false, title: '' });
      setNewMenu(null);
      openDoc(doc.id);
      fetchDocs();
    } catch (err) { setSnack({ msg: handleApiError(err), sev: 'error' }); }
  };

  const uploadFile = async (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('type', documentType);
    try {
      await api.post('/documents/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSnack({ msg: `Uploaded "${file.name}"`, sev: 'success' });
      fetchDocs();
    } catch (err) { setSnack({ msg: handleApiError(err), sev: 'error' }); }
  };

  const onUploadInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) uploadFile(f);
    e.target.value = '';
  };

  // Drag-drop
  const onDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.types.includes('Files')) {
      dragCounter.current++;
      setDragOver(true);
    }
  };
  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current <= 0) { dragCounter.current = 0; setDragOver(false); }
  };
  const onDragOver = (e: React.DragEvent) => { e.preventDefault(); };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current = 0; setDragOver(false);
    const files = Array.from(e.dataTransfer.files || []);
    files.forEach(f => uploadFile(f));
  };

  const renameDoc = async () => {
    if (!renameDialog.doc) return;
    try {
      await api.put(`/documents/${renameDialog.doc.id}`, { title: renameDialog.title });
      setRenameDialog({ open: false, doc: null, title: '' });
      fetchDocs();
    } catch (err) { setSnack({ msg: handleApiError(err), sev: 'error' }); }
  };

  const trashDoc = async (doc: SharedDoc) => {
    try { await api.delete(`/documents/${doc.id}`); fetchDocs(); }
    catch (err) { setSnack({ msg: handleApiError(err), sev: 'error' }); }
  };

  const restoreDoc = async (doc: SharedDoc) => {
    try { await api.post(`/documents/${doc.id}/restore`); fetchDocs(); }
    catch (err) { setSnack({ msg: handleApiError(err), sev: 'error' }); }
  };

  const permDeleteDoc = async (doc: SharedDoc) => {
    if (!confirm(`Permanently delete "${doc.title}"? This cannot be undone.`)) return;
    try { await api.delete(`/documents/${doc.id}/permanent`); fetchDocs(); }
    catch (err) { setSnack({ msg: handleApiError(err), sev: 'error' }); }
  };

  const duplicateDoc = async (doc: SharedDoc) => {
    try {
      const res = await api.post('/documents', { title: `Copy of ${doc.title}`, type: doc.type });
      setSnack({ msg: `Duplicated "${doc.title}"`, sev: 'success' });
      fetchDocs();
    } catch (err) { setSnack({ msg: handleApiError(err), sev: 'error' }); }
  };

  const bulkTrash = async () => {
    const ids = Array.from(selected);
    await Promise.allSettled(ids.map(id => api.delete(`/documents/${id}`)));
    setSelected(new Set());
    setSnack({ msg: `Moved ${ids.length} item${ids.length > 1 ? 's' : ''} to trash`, sev: 'success' });
    fetchDocs();
  };

  const bulkRestore = async () => {
    const ids = Array.from(selected);
    await Promise.allSettled(ids.map(id => api.post(`/documents/${id}/restore`)));
    setSelected(new Set()); fetchDocs();
  };

  const addShare = async () => {
    if (!shareDialog.doc || !shareEmail) return;
    try {
      await api.post(`/documents/${shareDialog.doc.id}/share`, { email: shareEmail, permission: sharePerm });
      setShareEmail('');
      const res = await api.get(`/documents/${shareDialog.doc.id}`);
      setShareDialog({ open: true, doc: extractData<SharedDoc>(res) });
      fetchDocs();
    } catch (err) { setSnack({ msg: handleApiError(err), sev: 'error' }); }
  };

  const removeShare = async (email: string) => {
    if (!shareDialog.doc) return;
    try {
      await api.delete(`/documents/${shareDialog.doc.id}/share/${encodeURIComponent(email)}`);
      const res = await api.get(`/documents/${shareDialog.doc.id}`);
      setShareDialog({ open: true, doc: extractData<SharedDoc>(res) });
      fetchDocs();
    } catch (err) { setSnack({ msg: handleApiError(err), sev: 'error' }); }
  };

  const toggleShareLink = async (doc: SharedDoc) => {
    try {
      if (doc.isShareLinkEnabled) await api.delete(`/documents/${doc.id}/share-link`);
      else await api.post(`/documents/${doc.id}/share-link`, { permission: 'view' });
      const res = await api.get(`/documents/${doc.id}`);
      if (shareDialog.open) setShareDialog({ open: true, doc: extractData<SharedDoc>(res) });
      fetchDocs();
    } catch (err) { setSnack({ msg: handleApiError(err), sev: 'error' }); }
  };

  const copyLink = (doc: SharedDoc) => {
    if (doc.shareLink) {
      navigator.clipboard.writeText(`${window.location.origin}/shared/${doc.shareLink}`);
      setSnack({ msg: 'Share link copied to clipboard', sev: 'success' });
    }
  };

  const ownerInitial = (d: SharedDoc) => (d.ownerEmail || 'U')[0].toUpperCase();

  // ─────────── Render ───────────
  return (
    <Box
      onDragEnter={onDragEnter} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}
      sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#f6f8fb', position: 'relative' }}
    >
      {/* Sticky header */}
      <Box sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: '#fff', borderBottom: '1px solid #e3e6ea' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 1.75 }}>
          <Box sx={{ width: 38, height: 38, bgcolor: accentSoft, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <TypeIcon sx={{ color: accent, fontSize: 22 }} />
          </Box>
          <Box sx={{ flex: '0 0 auto', minWidth: 0 }}>
            <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, lineHeight: 1.1, letterSpacing: '-0.01em', color: '#202124' }}>
              {typeWordPlural}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: '#5f6368' }}>
              Create, edit and share {typeWordPlural.toLowerCase()} with your team
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          {/* Search */}
          <TextField
            inputRef={searchRef}
            size="small" placeholder={`Search ${typeWordPlural.toLowerCase()} by name, owner, content...    /`}
            value={search} onChange={e => setSearch(e.target.value)}
            sx={{ width: 380, '& .MuiOutlinedInput-root': { bgcolor: '#f1f3f4', borderRadius: '24px', '& fieldset': { border: 'none' }, '&:hover fieldset': { border: 'none' }, '&.Mui-focused fieldset': { border: `1px solid ${accent}` } } }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#5f6368' }} /></InputAdornment> }}
          />
          <Tooltip title="Sort">
            <IconButton onClick={(e) => setSortMenu(e.currentTarget)} size="small"><SortIcon sx={{ fontSize: 20 }} /></IconButton>
          </Tooltip>
          <Menu anchorEl={sortMenu} open={!!sortMenu} onClose={() => setSortMenu(null)}>
            {(['updated', 'title', 'size'] as SortKey[]).map(k => (
              <MenuItem key={k} selected={sortKey === k} onClick={() => { setSortKey(k); setSortMenu(null); }}>
                {k === 'updated' ? 'Last modified' : k === 'title' ? 'Name' : 'Size'}
              </MenuItem>
            ))}
            <Divider />
            <MenuItem onClick={() => { setSortAsc(!sortAsc); setSortMenu(null); }}>
              {sortAsc ? 'Descending' : 'Ascending'}
            </MenuItem>
          </Menu>
          <Tooltip title="Hide / show templates">
            <IconButton onClick={() => setShowTemplates(s => !s)} size="small">
              <AppsIcon sx={{ fontSize: 20, color: showTemplates ? accent : '#5f6368' }} />
            </IconButton>
          </Tooltip>
          {/* View toggle */}
          <Box sx={{ display: 'flex', bgcolor: '#f1f3f4', borderRadius: '20px', p: '2px' }}>
            <IconButton size="small" onClick={() => setView('grid')} sx={{ borderRadius: '18px', bgcolor: view === 'grid' ? '#fff' : 'transparent', boxShadow: view === 'grid' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none' }}>
              <GridViewIcon sx={{ fontSize: 18, color: view === 'grid' ? accent : '#5f6368' }} />
            </IconButton>
            <IconButton size="small" onClick={() => setView('list')} sx={{ borderRadius: '18px', bgcolor: view === 'list' ? '#fff' : 'transparent', boxShadow: view === 'list' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none' }}>
              <ListViewIcon sx={{ fontSize: 18, color: view === 'list' ? accent : '#5f6368' }} />
            </IconButton>
          </Box>
          {/* New button */}
          <Button
            variant="contained" startIcon={<AddIcon />} endIcon={<ArrowDropDownIcon />}
            onClick={(e) => setNewMenu(e.currentTarget)}
            sx={{ bgcolor: accent, borderRadius: '24px', textTransform: 'none', px: 2.5, fontWeight: 500, boxShadow: `0 1px 3px ${accent}55`, '&:hover': { bgcolor: accent, filter: 'brightness(0.95)', boxShadow: `0 2px 6px ${accent}77` } }}
          >
            New
          </Button>
          <Menu anchorEl={newMenu} open={!!newMenu} onClose={() => setNewMenu(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
            <MenuItem onClick={() => createBlank()}>
              <ListItemIcon><TypeIcon sx={{ color: accent }} fontSize="small" /></ListItemIcon>
              <ListItemText primary={`Blank ${typeWord.toLowerCase()}`} />
            </MenuItem>
            <Divider />
            {templates.map(t => (
              <MenuItem key={t.key} onClick={() => createBlank(t.label)}>
                <ListItemIcon><Box sx={{ width: 18, height: 18, bgcolor: t.accent, borderRadius: '4px' }} /></ListItemIcon>
                <ListItemText primary={t.label} secondary={t.tag} />
              </MenuItem>
            ))}
            <Divider />
            <MenuItem component="label">
              <ListItemIcon><UploadIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Upload from computer" />
              <input type="file" hidden onChange={onUploadInput} accept={isSheet ? '.xlsx,.xls,.csv,.ods' : '.docx,.doc,.odt,.pdf,.txt'} />
            </MenuItem>
          </Menu>
        </Box>

        {/* Section tabs */}
        <Tabs
          value={section} onChange={(_, v) => { setSection(v); setSelected(new Set()); }}
          sx={{ px: 2, minHeight: 38, '& .MuiTab-root': { minHeight: 38, textTransform: 'none', fontSize: '0.85rem', fontWeight: 500, color: '#5f6368', minWidth: 'auto', px: 1.5 }, '& .Mui-selected': { color: `${accent} !important` }, '& .MuiTabs-indicator': { bgcolor: accent, height: 3, borderRadius: '3px 3px 0 0' } }}
        >
          <Tab value="all"     icon={<AppsIcon sx={{ fontSize: 16 }} />}    iconPosition="start" label={<>All <Chip size="small" label={counts.all}     sx={chipSx(section === 'all', accent)} /></>} />
          <Tab value="recent"  icon={<RecentIcon sx={{ fontSize: 16 }} />}  iconPosition="start" label={<>Recent</>} />
          <Tab value="starred" icon={<StarIcon sx={{ fontSize: 16 }} />}    iconPosition="start" label={<>Starred <Chip size="small" label={counts.starred} sx={chipSx(section === 'starred', accent)} /></>} />
          <Tab value="shared"  icon={<SharedIcon sx={{ fontSize: 16 }} />}  iconPosition="start" label={<>Shared with me <Chip size="small" label={counts.shared} sx={chipSx(section === 'shared', accent)} /></>} />
          <Tab value="trash"   icon={<DeleteIcon sx={{ fontSize: 16 }} />}  iconPosition="start" label={<>Trash <Chip size="small" label={counts.trash} sx={chipSx(section === 'trash', accent)} /></>} />
        </Tabs>
      </Box>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, py: 1, bgcolor: accentSoft, borderBottom: `1px solid ${accent}33` }}>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: accent, mr: 1 }}>
            {selected.size} selected
          </Typography>
          {section === 'trash' ? (
            <>
              <Button size="small" startIcon={<RestoreIcon />} onClick={bulkRestore} sx={{ textTransform: 'none', color: '#202124' }}>Restore</Button>
            </>
          ) : (
            <>
              <Button size="small" startIcon={<DeleteIcon />} onClick={bulkTrash} sx={{ textTransform: 'none', color: '#202124' }}>Move to trash</Button>
            </>
          )}
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={() => setSelected(new Set())} sx={{ textTransform: 'none', color: '#5f6368' }}>Clear</Button>
        </Box>
      )}

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        {/* Templates row (only for All / Recent and not searching) */}
        {showTemplates && (section === 'all' || section === 'recent') && !search && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.25 }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#5f6368', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Start a new {typeWord.toLowerCase()}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Button size="small" endIcon={<ArrowDropDownIcon />} sx={{ textTransform: 'none', color: '#5f6368', fontSize: '0.78rem' }}>Template gallery</Button>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 2 }}>
              {/* Blank */}
              <TemplateTile accent={accent} label="Blank" tag={`New ${typeWord.toLowerCase()}`} onClick={() => createBlank()} blank />
              {templates.map(t => (
                <TemplateTile key={t.key} accent={t.accent} label={t.label} tag={t.tag} onClick={() => createBlank(t.label)} />
              ))}
            </Box>
          </Box>
        )}

        {/* Section heading */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, mt: showTemplates ? 1 : 0 }}>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#5f6368', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {section === 'recent' ? 'Recently opened' : section === 'starred' ? 'Starred' : section === 'shared' ? 'Shared with me' : section === 'trash' ? 'In trash' : `My ${typeWordPlural.toLowerCase()}`}
          </Typography>
          <Box sx={{ flex: 1 }} />
          {filtered.length > 0 && (
            <Typography sx={{ fontSize: '0.75rem', color: '#80868b' }}>
              {filtered.length} item{filtered.length !== 1 ? 's' : ''}
            </Typography>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}><CircularProgress sx={{ color: accent }} /></Box>
        ) : filtered.length === 0 ? (
          <EmptyState section={section} typeWord={typeWord} accent={accent} onNew={() => createBlank()} />
        ) : view === 'grid' ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 2 }}>
            {filtered.map(d => (
              <FileCard
                key={d.id} doc={d} accent={accent} accentSoft={accentSoft}
                TypeIcon={TypeIcon}
                isStarred={starred.has(d.id)}
                isSelected={selected.has(d.id)}
                onOpen={() => !d.isDeleted && openDoc(d.id)}
                onMenu={(el) => setMenuAnchor({ el, doc: d })}
                onStar={() => toggleStar(d.id)}
                onSelect={() => toggleSelect(d.id)}
                showCheckbox={selected.size > 0}
                ownerInitial={ownerInitial(d)}
              />
            ))}
          </Box>
        ) : (
          <FileList
            docs={filtered} accent={accent} TypeIcon={TypeIcon}
            sortKey={sortKey} sortAsc={sortAsc}
            onSort={(k) => { if (k === sortKey) setSortAsc(!sortAsc); else { setSortKey(k); setSortAsc(false); } }}
            selected={selected} onToggleSelect={toggleSelect}
            starred={starred} onStar={toggleStar}
            onOpen={(d) => !d.isDeleted && openDoc(d.id)}
            onMenu={(el, d) => setMenuAnchor({ el, doc: d })}
            ownerInitial={ownerInitial}
          />
        )}
      </Box>

      {/* Drag-drop overlay */}
      {dragOver && (
        <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(255,255,255,0.94)', border: `3px dashed ${accent}`, borderRadius: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 100, pointerEvents: 'none' }}>
          <UploadIcon sx={{ fontSize: 64, color: accent, mb: 2 }} />
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 600, color: accent }}>
            Drop files to upload to {typeWordPlural}
          </Typography>
        </Box>
      )}

      {/* Context menu */}
      <Menu anchorEl={menuAnchor?.el || null} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        {menuAnchor && !menuAnchor.doc.isDeleted && [
          <MenuItem key="open" onClick={() => { openDoc(menuAnchor.doc.id); setMenuAnchor(null); }}>
            <ListItemIcon><OpenIcon fontSize="small" /></ListItemIcon><ListItemText>Open</ListItemText>
          </MenuItem>,
          <MenuItem key="star" onClick={() => { toggleStar(menuAnchor.doc.id); setMenuAnchor(null); }}>
            <ListItemIcon>{starred.has(menuAnchor.doc.id) ? <StarIcon fontSize="small" sx={{ color: '#f9ab00' }} /> : <StarBorderIcon fontSize="small" />}</ListItemIcon>
            <ListItemText>{starred.has(menuAnchor.doc.id) ? 'Remove star' : 'Add star'}</ListItemText>
          </MenuItem>,
          <MenuItem key="rename" onClick={() => { setRenameDialog({ open: true, doc: menuAnchor.doc, title: menuAnchor.doc.title }); setMenuAnchor(null); }}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon><ListItemText>Rename</ListItemText>
          </MenuItem>,
          <MenuItem key="dup" onClick={() => { duplicateDoc(menuAnchor.doc); setMenuAnchor(null); }}>
            <ListItemIcon><DuplicateIcon fontSize="small" /></ListItemIcon><ListItemText>Make a copy</ListItemText>
          </MenuItem>,
          <MenuItem key="share" onClick={() => { setShareDialog({ open: true, doc: menuAnchor.doc }); setMenuAnchor(null); }}>
            <ListItemIcon><ShareIcon fontSize="small" /></ListItemIcon><ListItemText>Share</ListItemText>
          </MenuItem>,
          <MenuItem key="link" onClick={() => { toggleShareLink(menuAnchor.doc); setMenuAnchor(null); }}>
            <ListItemIcon><LinkIcon fontSize="small" /></ListItemIcon>
            <ListItemText>{menuAnchor.doc.isShareLinkEnabled ? 'Disable share link' : 'Get share link'}</ListItemText>
          </MenuItem>,
          menuAnchor.doc.isShareLinkEnabled && (
            <MenuItem key="copylink" onClick={() => { copyLink(menuAnchor.doc); setMenuAnchor(null); }}>
              <ListItemIcon><CopyIcon fontSize="small" /></ListItemIcon><ListItemText>Copy share link</ListItemText>
            </MenuItem>
          ),
          <MenuItem key="dl" onClick={() => { window.open(`/api/documents/${menuAnchor.doc.id}/download`, '_blank'); setMenuAnchor(null); }}>
            <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon><ListItemText>Download</ListItemText>
          </MenuItem>,
          <Divider key="div" />,
          <MenuItem key="trash" onClick={() => { trashDoc(menuAnchor.doc); setMenuAnchor(null); }}>
            <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: '#d93025' }} /></ListItemIcon>
            <ListItemText sx={{ color: '#d93025' }}>Move to trash</ListItemText>
          </MenuItem>,
        ]}
        {menuAnchor && menuAnchor.doc.isDeleted && [
          <MenuItem key="restore" onClick={() => { restoreDoc(menuAnchor.doc); setMenuAnchor(null); }}>
            <ListItemIcon><RestoreIcon fontSize="small" /></ListItemIcon><ListItemText>Restore</ListItemText>
          </MenuItem>,
          <MenuItem key="permdel" onClick={() => { permDeleteDoc(menuAnchor.doc); setMenuAnchor(null); }}>
            <ListItemIcon><PermanentDeleteIcon fontSize="small" sx={{ color: '#d93025' }} /></ListItemIcon>
            <ListItemText sx={{ color: '#d93025' }}>Delete forever</ListItemText>
          </MenuItem>,
        ]}
      </Menu>

      {/* Rename dialog */}
      <Dialog open={renameDialog.open} onClose={() => setRenameDialog({ open: false, doc: null, title: '' })} maxWidth="xs" fullWidth>
        <DialogTitle>Rename</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth value={renameDialog.title} onChange={e => setRenameDialog(s => ({ ...s, title: e.target.value }))} onKeyDown={e => e.key === 'Enter' && renameDoc()} sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialog({ open: false, doc: null, title: '' })}>Cancel</Button>
          <Button variant="contained" onClick={renameDoc} sx={{ bgcolor: accent, '&:hover': { bgcolor: accent, filter: 'brightness(0.95)' } }}>OK</Button>
        </DialogActions>
      </Dialog>

      {/* Share dialog */}
      <Dialog open={shareDialog.open} onClose={() => setShareDialog({ open: false, doc: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Share "{shareDialog.doc?.title}"</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 1, mt: 1, mb: 2 }}>
            <TextField fullWidth size="small" placeholder="Add people by email" value={shareEmail} onChange={e => setShareEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && addShare()} />
            <TextField select size="small" value={sharePerm} onChange={e => setSharePerm(e.target.value as any)} sx={{ minWidth: 100 }}>
              <MenuItem value="view">Viewer</MenuItem>
              <MenuItem value="edit">Editor</MenuItem>
            </TextField>
            <Button variant="contained" onClick={addShare} disabled={!shareEmail} sx={{ bgcolor: accent, '&:hover': { bgcolor: accent, filter: 'brightness(0.95)' } }}>Send</Button>
          </Box>
          {shareDialog.doc?.sharedWith && shareDialog.doc.sharedWith.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>People with access</Typography>
              {shareDialog.doc.sharedWith.map((s, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
                  <Avatar sx={{ width: 28, height: 28, mr: 1, bgcolor: accent, fontSize: '0.75rem' }}>{s.email[0].toUpperCase()}</Avatar>
                  <Typography variant="body2" sx={{ flex: 1 }}>{s.email}</Typography>
                  <Chip size="small" label={s.permission} sx={{ mr: 1 }} />
                  <IconButton size="small" onClick={() => removeShare(s.email)}><DeleteIcon fontSize="small" /></IconButton>
                </Box>
              ))}
            </>
          )}
          {shareDialog.doc && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="subtitle2">Share link</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {shareDialog.doc.isShareLinkEnabled ? 'Anyone with the link can access' : 'Disabled'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {shareDialog.doc.isShareLinkEnabled && (
                    <Button size="small" startIcon={<CopyIcon />} onClick={() => copyLink(shareDialog.doc!)}>Copy link</Button>
                  )}
                  <Button size="small" variant={shareDialog.doc.isShareLinkEnabled ? 'outlined' : 'contained'} onClick={() => toggleShareLink(shareDialog.doc!)} sx={!shareDialog.doc.isShareLinkEnabled ? { bgcolor: accent, '&:hover': { bgcolor: accent, filter: 'brightness(0.95)' } } : {}}>
                    {shareDialog.doc.isShareLinkEnabled ? 'Disable' : 'Enable'}
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareDialog({ open: false, doc: null })}>Done</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={3500} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {snack ? <Alert onClose={() => setSnack(null)} severity={snack.sev} sx={{ width: '100%' }}>{snack.msg}</Alert> : <span />}
      </Snackbar>
    </Box>
  );
};

const chipSx = (active: boolean, accent: string) => ({
  ml: 0.75, height: 18, fontSize: '0.7rem', fontWeight: 600,
  bgcolor: active ? accent : '#e8eaed', color: active ? '#fff' : '#5f6368',
  '& .MuiChip-label': { px: 0.75 },
});

// ─────────── Subcomponents ───────────

const TemplateTile: React.FC<{ accent: string; label: string; tag: string; onClick: () => void; blank?: boolean }> = ({ accent, label, tag, onClick, blank }) => (
  <Paper elevation={0} onClick={onClick} sx={{ cursor: 'pointer', border: '1px solid #e3e6ea', borderRadius: 2, overflow: 'hidden', transition: 'all 0.15s', '&:hover': { borderColor: accent, boxShadow: `0 2px 10px ${accent}33`, transform: 'translateY(-2px)' } }}>
    <Box sx={{ height: 130, bgcolor: blank ? '#f1f3f4' : '#fff', display: 'flex', alignItems: blank ? 'center' : 'flex-start', justifyContent: 'center', position: 'relative', borderBottom: '1px solid #e3e6ea', overflow: 'hidden' }}>
      {blank ? (
        <Box sx={{ width: 44, height: 44, bgcolor: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <AddIcon sx={{ color: accent }} />
        </Box>
      ) : (
        <Box sx={{ width: '100%', p: 1.5 }}>
          <Box sx={{ fontSize: '0.7rem', fontWeight: 700, color: accent, mb: 0.5 }}>{label}</Box>
          {[100, 90, 95, 75, 88, 60].map((w, i) => (
            <Box key={i} sx={{ height: 4, bgcolor: '#e8eaed', borderRadius: 1, mb: 0.5, width: `${w}%` }} />
          ))}
        </Box>
      )}
    </Box>
    <Box sx={{ px: 1.25, py: 1 }}>
      <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: '#202124', lineHeight: 1.2 }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.7rem', color: '#80868b' }}>{tag}</Typography>
    </Box>
  </Paper>
);

const FileCard: React.FC<{
  doc: SharedDoc; accent: string; accentSoft: string; TypeIcon: any;
  isStarred: boolean; isSelected: boolean; showCheckbox: boolean; ownerInitial: string;
  onOpen: () => void; onMenu: (el: HTMLElement) => void; onStar: () => void; onSelect: () => void;
}> = ({ doc, accent, accentSoft, TypeIcon, isStarred, isSelected, showCheckbox, ownerInitial, onOpen, onMenu, onStar, onSelect }) => (
  <Paper elevation={0} sx={{ position: 'relative', border: `1px solid ${isSelected ? accent : '#e3e6ea'}`, borderRadius: 2, overflow: 'hidden', cursor: 'pointer', transition: 'all 0.15s', bgcolor: isSelected ? accentSoft : '#fff', '&:hover': { borderColor: accent, boxShadow: `0 2px 10px ${accent}33` }, '&:hover .file-actions': { opacity: 1 } }}>
    {/* Checkbox */}
    <Box className="file-actions" sx={{ position: 'absolute', top: 8, left: 8, opacity: showCheckbox || isSelected ? 1 : 0, transition: 'opacity 0.15s', zIndex: 2 }}>
      <Checkbox size="small" checked={isSelected} onClick={(e) => { e.stopPropagation(); onSelect(); }} sx={{ p: 0.5, bgcolor: '#fff', borderRadius: '4px', '&.Mui-checked': { color: accent } }} />
    </Box>
    {/* Star */}
    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onStar(); }} sx={{ position: 'absolute', top: 6, right: 36, zIndex: 2, p: 0.5, color: isStarred ? '#f9ab00' : '#dadce0', '&:hover': { color: isStarred ? '#f9ab00' : '#5f6368', bgcolor: 'rgba(0,0,0,0.04)' } }}>
      {isStarred ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
    </IconButton>
    {/* Menu */}
    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onMenu(e.currentTarget); }} sx={{ position: 'absolute', top: 6, right: 6, zIndex: 2, p: 0.5, color: '#5f6368' }}>
      <MoreIcon fontSize="small" />
    </IconButton>
    {/* Preview */}
    <Box onClick={onOpen} sx={{ height: 140, bgcolor: accentSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: '1px solid #e3e6ea' }}>
      <TypeIcon sx={{ fontSize: 56, color: accent, opacity: 0.6 }} />
    </Box>
    {/* Footer */}
    <Box onClick={onOpen} sx={{ p: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <TypeIcon sx={{ fontSize: 14, color: accent, flexShrink: 0 }} />
        <Typography noWrap sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#202124', flex: 1 }}>{doc.title}</Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
        <Avatar sx={{ width: 18, height: 18, fontSize: '0.6rem', bgcolor: '#5f6368' }}>{ownerInitial}</Avatar>
        <Typography sx={{ fontSize: '0.72rem', color: '#5f6368' }}>{formatRelative(doc.updatedAt)}</Typography>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: '0.7rem', color: '#80868b' }}>{formatSize(doc.fileSize)}</Typography>
      </Box>
    </Box>
  </Paper>
);

const FileList: React.FC<{
  docs: SharedDoc[]; accent: string; TypeIcon: any;
  sortKey: SortKey; sortAsc: boolean; onSort: (k: SortKey) => void;
  selected: Set<string>; onToggleSelect: (id: string) => void;
  starred: Set<string>; onStar: (id: string) => void;
  onOpen: (d: SharedDoc) => void; onMenu: (el: HTMLElement, d: SharedDoc) => void;
  ownerInitial: (d: SharedDoc) => string;
}> = ({ docs, accent, TypeIcon, sortKey, sortAsc, onSort, selected, onToggleSelect, starred, onStar, onOpen, onMenu, ownerInitial }) => {
  const HeaderCell: React.FC<{ k?: SortKey; w?: any; children: React.ReactNode; right?: boolean }> = ({ k, w, children, right }) => (
    <Box onClick={k ? () => onSort(k) : undefined} sx={{ width: w, display: 'flex', alignItems: 'center', justifyContent: right ? 'flex-end' : 'flex-start', cursor: k ? 'pointer' : 'default', fontSize: '0.74rem', fontWeight: 600, color: '#5f6368', textTransform: 'uppercase', letterSpacing: '0.4px', userSelect: 'none', '&:hover': k ? { color: accent } : {} }}>
      {children}
      {k && sortKey === k && <Box component="span" sx={{ ml: 0.25, fontSize: '0.7rem' }}>{sortAsc ? '▲' : '▼'}</Box>}
    </Box>
  );
  return (
    <Paper elevation={0} sx={{ border: '1px solid #e3e6ea', borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.25, borderBottom: '1px solid #e3e6ea', bgcolor: '#fafbfc' }}>
        <Box sx={{ width: 28 }} />
        <HeaderCell k="title" w={undefined}><Box sx={{ flex: 1 }}>Name</Box></HeaderCell>
        <Box sx={{ flex: 1 }} />
        <HeaderCell w={140}>Owner</HeaderCell>
        <HeaderCell k="updated" w={140}>Last modified</HeaderCell>
        <HeaderCell k="size" w={80} right>Size</HeaderCell>
        <Box sx={{ width: 32 }} />
      </Box>
      {docs.map(d => (
        <Box key={d.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1, borderBottom: '1px solid #f1f3f4', cursor: 'pointer', bgcolor: selected.has(d.id) ? `${accent}11` : '#fff', '&:hover': { bgcolor: selected.has(d.id) ? `${accent}22` : '#f8f9fa' }, '&:last-child': { borderBottom: 'none' }, '&:hover .row-checkbox': { opacity: 1 } }} onDoubleClick={() => onOpen(d)}>
          <Box sx={{ width: 28, display: 'flex', justifyContent: 'center' }}>
            <Checkbox size="small" checked={selected.has(d.id)} onClick={(e) => { e.stopPropagation(); onToggleSelect(d.id); }} className="row-checkbox" sx={{ p: 0.25, opacity: selected.has(d.id) ? 1 : 0, '&.Mui-checked': { color: accent } }} />
          </Box>
          <TypeIcon sx={{ color: accent, fontSize: 22, flexShrink: 0 }} />
          <Box onClick={() => onOpen(d)} sx={{ flex: 1, minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: '0.88rem', color: '#202124' }}>{d.title}</Typography>
          </Box>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onStar(d.id); }} sx={{ p: 0.5, color: starred.has(d.id) ? '#f9ab00' : '#dadce0', '&:hover': { color: '#f9ab00' } }}>
            {starred.has(d.id) ? <StarIcon sx={{ fontSize: 18 }} /> : <StarBorderIcon sx={{ fontSize: 18 }} />}
          </IconButton>
          <Box sx={{ width: 140, display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Avatar sx={{ width: 22, height: 22, fontSize: '0.7rem', bgcolor: '#5f6368' }}>{ownerInitial(d)}</Avatar>
            <Typography noWrap sx={{ fontSize: '0.78rem', color: '#5f6368' }}>{d.ownerEmail || 'me'}</Typography>
          </Box>
          <Typography sx={{ width: 140, fontSize: '0.78rem', color: '#5f6368' }}>{formatRelative(d.updatedAt)}</Typography>
          <Typography sx={{ width: 80, fontSize: '0.78rem', color: '#5f6368', textAlign: 'right' }}>{formatSize(d.fileSize)}</Typography>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onMenu(e.currentTarget, d); }} sx={{ p: 0.5 }}><MoreIcon fontSize="small" /></IconButton>
        </Box>
      ))}
    </Paper>
  );
};

const EmptyState: React.FC<{ section: SectionTab; typeWord: string; accent: string; onNew: () => void }> = ({ section, typeWord, accent, onNew }) => {
  const map: Record<SectionTab, { title: string; sub: string; cta?: string }> = {
    all:     { title: `No ${typeWord.toLowerCase()}s yet`,        sub: 'Create your first one or upload a file.', cta: `New ${typeWord.toLowerCase()}` },
    recent:  { title: 'Nothing recent',                            sub: `${typeWord}s you open will show up here.` },
    starred: { title: 'No starred items',                          sub: 'Click the star on any file to keep it close.' },
    shared:  { title: 'Nothing shared with you yet',               sub: 'When teammates share files, they appear here.' },
    trash:   { title: 'Trash is empty',                            sub: 'Items you delete stay here for 30 days.' },
  };
  const e = map[section];
  return (
    <Box sx={{ textAlign: 'center', py: 8 }}>
      <Box sx={{ width: 96, height: 96, mx: 'auto', mb: 2, borderRadius: '50%', bgcolor: `${accent}11`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {section === 'starred' ? <StarBorderIcon sx={{ fontSize: 44, color: accent }} /> :
         section === 'shared' ? <SharedIcon sx={{ fontSize: 44, color: accent }} /> :
         section === 'trash' ? <DeleteIcon sx={{ fontSize: 44, color: accent }} /> :
         section === 'recent' ? <RecentIcon sx={{ fontSize: 44, color: accent }} /> :
         <DocIcon sx={{ fontSize: 44, color: accent }} />}
      </Box>
      <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#202124' }}>{e.title}</Typography>
      <Typography sx={{ fontSize: '0.9rem', color: '#5f6368', mt: 0.5 }}>{e.sub}</Typography>
      {e.cta && (
        <Button onClick={onNew} variant="contained" startIcon={<AddIcon />} sx={{ mt: 2.5, bgcolor: accent, borderRadius: '24px', textTransform: 'none', '&:hover': { bgcolor: accent, filter: 'brightness(0.95)' } }}>{e.cta}</Button>
      )}
    </Box>
  );
};

export default DocsPage;
